import React, { useEffect, useState } from 'react';
import { NotificationManager } from '../components/common/react-notifications';
import { Status } from '../constants/status';
import { useUserContext } from '../context/UserContext';
import productAPI from '../services/API/product';
import { useBasicTableState } from './useBasicTableState';

export const useProductTable = ({ status } = {}) => {
  const { user, setUser, loadingUserDetails, availableFreeSlot } =
    useUserContext();
  const {
    detailOpen,
    setDetailOpen,
    loadingItems: loadingProducts,
    setLoadingItems: setLoadingProducts,
    items: products,
    setItems: setProducts,
    selectedItemIndex: selectedProductIndex,
    setSelectedItemIndex: setSelectedProductIndex,
    selectedItem: selectedProduct,
    loadingItemsHistory: loadingProductsHistory,
    setLoadingItemsHistory: setLoadingProductsHistory,
    itemsHistory: productsHistory,
    setItemsHistory: setProductsHistory,
    selectedItemHistoryIndex: selectedProductHistoryIndex,
    setSelectedItemHistoryIndex: setSelectedProductHistoryIndex,
    selectedItemHistory: selectedProductHistory,
    loadingGetProductStatus: loadProductStatuses,
    setLoadingGetProductStatus: setLoadProductStatuses,
    productStatuses: productStatusList,
    setProductStatuses: setProductStatusList,
  } = useBasicTableState();

  // selected product status
  const [selectedStatus, setSelectedStatus] = useState(status ?? '');

  // contains the state of the bttons in the product update details
  const [detailPageButtonStates, setDetailPageButtonStates] = useState({
    updatingButton: '',
  });

  useEffect(() => {
    setSelectedStatus(status);
  }, [status]);

  // loads products on start
  useEffect(() => {
    if (selectedStatus === 'rental_history') {
      setLoadingProductsHistory(true);
      loadRentalsHistory();
    } else {
      setLoadingProducts(true);
      loadProducts();
    }
    loadProductStatusList();
  }, [selectedStatus]);

  // ###############  Start of Product Operations #############
  const loadProducts = (status) => {
    if (selectedStatus !== 'rental_history' && status !== 'rental_history') {
      let dataToSend = {
        status: status ? status : selectedStatus,
        user_id: user.id ?? 340,
      };
      if (status === 'consignments' || selectedStatus === 'consignments') {
        dataToSend = {
          ...dataToSend,
          count: false,
        };
      }
      productAPI
        .getProductsByStatus(dataToSend)
        .then((data) => {
          setProducts(data);
        })
        .catch((err) => {
          console.log(err);
          if (err.message.toLowerCase().includes('network error')) {
            NotificationManager.error({
              message: 'There is no internet connection!',
            });
          } else {
            NotificationManager.error({
              message: 'Something went wrong, please try again',
            });
          }
          setProducts([]);
          setProductsHistory([]);
          setLoadingProducts(false);
          setLoadingProductsHistory(false);
        })
        .finally(() => {
          setLoadingProducts(false);
          setLoadingProductsHistory(false);
        });
    }
  };

  const loadProductStatusList = () => {
    // productAPI
    //   .getProductStatuses()
    //   .then((data) => {
    const defaultStatus = [
      {
        status: 'consignments',
        status_display_user: 'view all',
      },
    ];
    const STATUS_DATA_LIST = [
      {
        status: 'draft_by_lender',
        status_display_user: 'draft',
      },
      {
        status: 'applied_c',
        status_display_user: 'applied',
      },
      { status: 'approved1_c', status_display_user: 'approved' },
      { status: 'rejected1_c', status_display_user: 'rejected' },

      {
        status: 'shipped_c',
        status_display_user: 'shipped',
      },
      {
        status: 'withdrawn_c',
        status_display_user: 'withdrawn',
      },
      {
        status: 'listed',
        status_display_user: 'listed',
      },
      {
        status: 'bought',
        status_display_user: 'purchased',
      },
      {
        status: 'rental_delivered',
        status_display_user: 'out on rent',
      },
      {
        status: 'claiming_c',
        status_display_user: 'reclaiming',
      },
      {
        status: 'claimed_c',
        status_display_user: 'reclaimed',
      },
      {
        status: 'returning_c',
        status_display_user: 'returned',
      },
      {
        status: 'retired',
        status_display_user: 'retired',
      },
      {
        status: 'approved2_c',
        status_display_user: 'processing',
      },
      {
        status: 'rejected2_c',
        status_display_user: 'failed inspection',
      },
    ];
    const sorted = STATUS_DATA_LIST.sort((x, y) =>
      x.status_display_user.localeCompare(y.status_display_user)
    );
    setProductStatusList(defaultStatus.concat(...sorted));
    setLoadProductStatuses(false);
    // })
    // .catch((err) => {
    //   console.log(err);
    //   if (err.message.toLowerCase().includes('network error')) {
    //     NotificationManager.error({
    //       message: 'There is no internet connection!',
    //     });
    //   } else {
    //     NotificationManager.error({
    //       message: 'Something went wrong, please try again',
    //     });
    //   }
    //   setProductStatusList([]);
    //   setLoadProductStatuses(false);
    // })
    // .finally(() => {
    //   setLoadProductStatuses(false);
    // });
  };

  const loadRentalsHistory = () => {
    productAPI
      .getProductsHistoryByStatus({
        status: status ? status : selectedStatus,
        user_id: user.id ?? 340,
        count: false,
      })
      .then((data) => {
        setProductsHistory(data);
      })
      .catch((err) => {
        console.log(err);
        if (err.message.toLowerCase().includes('network error')) {
          NotificationManager.error({
            message: 'There is no internet connection!',
          });
        } else {
          NotificationManager.error({
            message: 'Something went wrong, please try again',
          });
        }
        setProductsHistory([]);
        setProducts([]);
        setLoadingProductsHistory(false);
        setLoadingProducts(false);
      })
      .finally(() => {
        setLoadingProductsHistory(false);
        setLoadingProducts(false);
      });
  };

  const addProduct = async (buttonId, newData, images) => {
    const {
      title,
      description,
      color,
      size,
      size_type,
      gender,
      brand,
      commission_percent,
      store_commission_percent,
      rent_earnings_per_week,
      rent_per_week,
      status,
      ...rest
    } = newData;

    // // dummy data
    // const consigned_on = "2020-04-01";
    // const consigned_until = "2021-03-28";

    const product = {
      status,
      product: {
        ...rest,
        consigned_by_user_id: user.id,
        // last_rented_by_user_id: user.id,
        commission_percent: store_commission_percent || commission_percent,
        rent_per_week: rent_earnings_per_week || rent_per_week,
        data: {
          title,
          description,
          color,
          size,
          size_type,
          gender,
          brand,
        },
      },
    };

    setDetailPageButtonStates({
      ...detailPageButtonStates,
      updatingButton: buttonId,
    });

    try {
      const { success, id, ...rest } = await productAPI.addProduct(product);
      await productAPI.addProductImages(
        id,
        images.map((img) => img.url)
      );
      loadProducts();
      setDetailOpen(false);
      // let's show the notification here
      NotificationManager.success({
        message: 'Your listing application has been submitted!',
      });
      setSelectedProductIndex(0); // setting the current to be the selected one
    } catch (error) {
      console.error(error);
    }

    setDetailPageButtonStates({
      ...detailPageButtonStates,
      updatingButton: '',
    });
  };

  const refreshProducts = () => {
    setLoadingProducts(true);
    loadProducts();
  };

  const refreshProductsHistory = () => {
    setLoadingProductsHistory(true);
    loadRentalsHistory();
  };

  const formatData = (data) => {
    data.data = {
      title: data.title,
      description: data.description,
      color: data.color,
      size: data.size,
      size_type: data.size_type,
      gender: data.gender,
      brand: data.brand,
    };
    delete data.title;
    delete data.description;
    delete data.color;
    delete data.size;
    delete data.size_type;
    delete data.gender;
    delete data.brand;
    return data;
  };

  const updateProductDetails = (
    newDetails,
    buttonId,
    handlers = {},
    disableRedirect = false
  ) => {
    setDetailPageButtonStates({
      ...detailPageButtonStates,
      updatingButton: buttonId,
    });
    const { id, ...product } = selectedProduct;
    delete product.tableData;

    const checkData = {
      ...newDetails,
    };

    // let productToSubmit = { id, product: { ...product, ...newDetails } };
    let productToSubmit = { id, product: { ...formatData(checkData) } };

    productAPI
      .updateProduct(productToSubmit)
      .then((data) => {
        if (data.success) {
          loadProducts();

          // show the result of the operation
          NotificationManager.success({
            message: 'Successfully updated!',
            customClassName: 'filled',
          });

          if (!disableRedirect) {
            setDetailOpen(false);
          }
          // setSelectedProduct(newProd);
          if (typeof handlers.afterSuccess === 'function') {
            handlers.afterSuccess(data);
          }
        } else {
          NotificationManager.error({
            message: 'Something went wrong!',
          });
        }
      })
      .catch(handleError)
      .finally(() => {
        setDetailPageButtonStates({
          ...detailPageButtonStates,
          updatingButton: '',
        });

        if (typeof handlers.afterAny === 'function') {
          handlers.afterAny();
        }
      });
  };

  const updateProductStatus = (newUpdate, buttonId, afterSuccess) => {
    setDetailPageButtonStates({
      ...detailPageButtonStates,
      updatingButton: buttonId,
    });

    let theApiHandler = productAPI.updateProductStatus;

    theApiHandler(newUpdate)
      .then((response) => {
        if (response.success) {
          NotificationManager.success({
            message: 'Updated!',
          });

          loadProducts();
          setDetailOpen(false);

          if (typeof afterSuccess === 'function') {
            afterSuccess(response);
          }
        }
      })
      .catch(handleError)
      .finally(() => {
        setDetailPageButtonStates({
          ...detailPageButtonStates,
          updatingButton: '',
        });
      });
  };

  const deleteProduct = (newUpdate, buttonId) => {
    setDetailPageButtonStates({
      ...detailPageButtonStates,
      updatingButton: buttonId,
    });

    let theApiHandler = productAPI.deleteProduct;

    theApiHandler(newUpdate)
      .then((response) => {
        if (response.status === Status.deleted) {
          NotificationManager.success({
            message: 'Deleted!',
          });
          loadProducts();
          setDetailOpen(false);
        }
      })
      .catch(handleError)
      .finally(() => {
        setDetailPageButtonStates({
          ...detailPageButtonStates,
          updatingButton: '',
        });
      });
  };

  // helper
  const handleError = (err) => {
    if (err.message.toLowerCase().includes('network error')) {
      NotificationManager.error({
        message: 'There is no internet connection!',
      });
    } else {
      NotificationManager.error({
        message: 'Something went wrong, please try again',
      });
    }
  };

  return {
    detailOpen,
    setDetailOpen,
    loadingProducts,
    setLoadingProducts,
    products,
    setProducts,
    selectedProductIndex,
    setSelectedProductIndex,
    selectedProduct,
    selectedStatus,
    setSelectedStatus,

    detailPageButtonStates,
    setDetailPageButtonStates,
    updateProductDetails,
    updateProductStatus,
    deleteProduct,
    addProduct,

    refreshProducts,

    loadingProductsHistory,
    setLoadingProductsHistory,
    productsHistory,
    setProductsHistory,
    selectedProductHistoryIndex,
    setSelectedProductHistoryIndex,
    selectedProductHistory,
    refreshProductsHistory,
    loadProductStatuses,
    setLoadProductStatuses,
    productStatusList,
    setProductStatusList,
  };
};
